import React, { useContext } from 'react';
import { ExternalLink } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { UsuarioContext } from '../../providers/personaContext';
import { puedeAbrirFichas, puedeVerSoporte } from '../../providers/rolesFicha';

/**
 * LinkFichaPersona — Fase 24
 *
 * Link a la ficha del trabajador (form de edición de persona), abre en nueva
 * pestaña. Branch automático entre empleado interno y colaborador externo según
 * el ámbito de la relación. Aterriza en la tab "Soporte" (tab=3): datos técnicos
 * del equipo (AnyDesk, BitLocker, credenciales). El panel Soporte mantiene su
 * candado de password — el deep-link no expone datos sensibles.
 *
 * Props:
 *   personaId  · number|null — sin id → renderiza texto plano (fallback graceful)
 *   nombre     · string — texto visible
 *   ambito     · 'interno' | 'colaborador' — determina la ruta destino
 *   tab        · number (default 3)
 *   className  · string — pass-through
 *   showIcon   · boolean (default true)
 *   tooltip    · string — title opcional
 *
 * Quien no puede abrir fichas (rolesFicha.js) ve el nombre como texto: un enlace que acaba en
 * «no tienes acceso» es una puerta pintada. Lo usa el panel del técnico, al que se llega por haber
 * sido técnico, no por el rol (hotfix 23/10).
 *
 * Y quien abre fichas pero no tiene la pestaña Soporte (Recursos Humanos) no recibe `?tab=3`: la
 * ficha lo dejaba en una pestaña que no existe para él, en blanco (3/11). Aterriza en Personal.
 */
export default function LinkFichaPersona({
  personaId,
  nombre,
  ambito = 'interno',
  tab = 3,
  className = '',
  showIcon = true,
  tooltip,
}) {
  const sesion = useContext(UsuarioContext);
  // Fallback graceful: sin id, sin nombre o sin poder abrir fichas → texto plano, sin link ni icono.
  if (!personaId || !nombre || !puedeAbrirFichas(sesion?.persona?.rol)) {
    return <span className={className}>{nombre || '—'}</span>;
  }

  const basePath = ambito === 'colaborador'
    ? '/gestionpersonas/personas-colaboradoras/editarColaborador'
    : '/gestionpersonas/personas/editarPersona';

  // `tab` 3 (o 'soporte') es Soporte: sólo para quien la tiene.
  const esSoporte = String(tab) === '3' || tab === 'soporte';
  const conTab = tab !== null && tab !== undefined && (!esSoporte || puedeVerSoporte(sesion?.persona?.rol));
  const href = conTab ? `${basePath}/${personaId}?tab=${tab}` : `${basePath}/${personaId}`;
  const finalTooltip = tooltip ?? `Ver ficha de ${nombre} · Se abre en pestaña nueva`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={finalTooltip}
      className={cn(
        'inline-flex items-center gap-1 text-slate-700 hover:text-blue-600',
        'hover:underline underline-offset-2 transition-colors',
        className
      )}
      onClick={(e) => e.stopPropagation()} // no propagar a la fila padre (modal, etc.)
    >
      <span>{nombre}</span>
      {showIcon && (
        <ExternalLink className="w-3 h-3 text-slate-400 shrink-0" aria-hidden="true" />
      )}
    </a>
  );
}
